/**
 * Which wallet badge(s) a device should be offered for the Labcorp Patient Pass.
 * `'both'` is the honest answer when the platform is unknown or is neither an
 * Apple nor an Android one (Windows, Linux, ChromeOS): the pass is usually being
 * saved for a PHONE the desktop cannot see, so the choice belongs to the user.
 */
export type OrbiWalletPlatform = 'apple' | 'google' | 'both';

/**
 * Picks the wallet badge to show from the browser's user agent (design review
 * 08/20: "render the platform-specific wallet if the OS is identifiable,
 * otherwise show both"). Apple platforms get Apple Wallet, Android gets Google
 * Wallet, and anything else gets the pair — the portal's Labcorp ID page shows
 * both badges unconditionally, which puts an Apple Wallet button in front of
 * every Android user.
 *
 * The two signals are computed INDEPENDENTLY and only decide the answer when
 * they disagree, so a user agent that claims both (a spoofed or rewritten UA,
 * an embedded webview) falls back to showing both rather than to whichever test
 * happened to run first.
 *
 * The user agent is the only input. `navigator.platform` is deprecated and adds
 * nothing here: Chrome's UA reduction still emits `Macintosh` and `Android`, and
 * the platform string's Android value (`Linux armv8l`) is shared with ordinary
 * ARM Linux, so it would invent Google badges for Linux desktops.
 *
 * `Macintosh` counts as Apple, which also (correctly) catches iPadOS 13+ Safari
 * — it reports itself as a Mac, and both answers are `'apple'` anyway. A desktop
 * Mac is deliberately included: macOS has had a Wallet app since Ventura, Safari
 * opens a `.pkpass` into it, and a Mac user is overwhelmingly likely to be
 * carrying the iPhone the pass is really for.
 *
 * NOT the repo's `UserAgent` service: its `isAndroid` is `/Android|Mobile/i`,
 * which is true on an iPhone, and it has no notion of macOS — so it cannot
 * answer "which wallet", and widening those flags would change behavior for
 * every existing consumer of that service.
 */
export function resolveOrbiWalletPlatform(
  userAgent?: string | null
): OrbiWalletPlatform {
  const ua = userAgent ?? '';
  const isApple = /iPhone|iPad|iPod|Macintosh/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  if (isApple === isAndroid) {
    return 'both';
  }
  return isApple ? 'apple' : 'google';
}
